import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, X } from 'lucide-react';
import { useLandingTheme } from '../hooks/useLandingTheme';

export type LegalDocKey = 'termsOfUse' | 'termsOfSale' | 'privacyPolicy';

interface Block {
  type: 'p' | 'ul';
  text?: string;
  items?: string[];
}

interface Section {
  heading: string;
  blocks: Block[];
}

interface LegalModalProps {
  docKey: LegalDocKey | null;
  onClose: () => void;
}

const LegalModal: React.FC<LegalModalProps> = ({ docKey, onClose }) => {
  const { t } = useTranslation();
  const isDark = useLandingTheme();

  useEffect(() => {
    if (!docKey) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [docKey, onClose]);

  if (!docKey) return null;

  const title = t(`legal.${docKey}.title`);
  const subtitle = t(`legal.${docKey}.subtitle`);
  const sections = t(`legal.${docKey}.sections`, { returnObjects: true }) as Section[];

  const modalBg = isDark ? '#0F172B' : '#FFFFFF';
  const modalBorder = isDark ? '#314158' : '#E2E8F0';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const bodyColor = isDark ? '#CBD5E1' : '#334155';
  const mutedText = isDark ? '#90A1B9' : '#62748E';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const headerBg = isDark ? '#1D293D' : '#F8FAFC';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: isDark ? 'rgba(2, 6, 24, 0.7)' : 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: modalBg,
          border: `1px solid ${modalBorder}`,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '820px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: isDark
            ? '0 25px 50px rgba(2,6,24,0.5)'
            : '0 25px 50px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 24px',
            borderBottom: `1px solid ${sectionBorder}`,
            background: headerBg,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '10px',
              background: iconContainerBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FileText size={18} style={{ color: iconColor }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="font-geist"
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: titleColor,
                lineHeight: '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </div>
            <div style={{ fontSize: '12px', color: mutedText, marginTop: '2px' }}>
              {subtitle}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('legal.close')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: mutedText,
              flexShrink: 0,
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div
          className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 28px 28px',
            minHeight: 0,
          }}
        >
          {Array.isArray(sections) &&
            sections.map((section, idx) => (
              <section
                key={idx}
                style={{ marginBottom: idx === sections.length - 1 ? 0 : '24px' }}
              >
                <h3
                  className="font-geist"
                  style={{
                    fontSize: '14px',
                    fontWeight: 700,
                    color: titleColor,
                    marginBottom: '10px',
                    letterSpacing: '0.02em',
                  }}
                >
                  {section.heading}
                </h3>
                {section.blocks.map((block, bIdx) => {
                  if (block.type === 'p') {
                    return (
                      <p
                        key={bIdx}
                        style={{
                          fontSize: '13px',
                          color: bodyColor,
                          lineHeight: '1.65',
                          marginBottom: '10px',
                        }}
                      >
                        {block.text}
                      </p>
                    );
                  }
                  if (block.type === 'ul' && block.items) {
                    return (
                      <ul
                        key={bIdx}
                        style={{
                          listStyle: 'disc',
                          paddingLeft: '20px',
                          marginBottom: '10px',
                        }}
                      >
                        {block.items.map((item, iIdx) => (
                          <li
                            key={iIdx}
                            style={{
                              fontSize: '13px',
                              color: bodyColor,
                              lineHeight: '1.65',
                              marginBottom: '4px',
                            }}
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })}
              </section>
            ))}
        </div>
      </div>
    </div>
  );
};

export default LegalModal;
